/**
 * @file CreditLimitManagement.js
 * @version 2.1.1
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2025-11-03
 * @modified 2026-09-29
 */

import { useState, useEffect } from 'react';
import { API_URL } from '../config';
import './SearchLayout.css';
import './CreditLimitManagement.css';
import { showAlert } from './Alert';
import ContactModal from './ContactModal';
import { useSlideOutButton } from './SlideOutButton';
import ReactDOM from 'react-dom';
import LoadingSpinner from './LoadingSpinner';
import { hasPermission } from '../utils/permissions';
import AccessDenied from './AccessDenied';
import { activeHeaderFilters, clearFiltersClass } from '../utils/activeFilters';

function CreditLimitManagement({ user }) {
  useSlideOutButton();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [sortField, setSortField] = useState('companyName');
  const [sortDirection, setSortDirection] = useState('asc');
  const [filterOverLimit, setFilterOverLimit] = useState(false);

  // The only filter is the over-limit toggle; sort order is not a filter.
  const headerFilters = activeHeaderFilters(
    { overLimit: filterOverLimit }, { overLimit: false }, { labels: { overLimit: 'Over limit' } }
  );
  const filtersApplied = headerFilters.count > 0;

  useEffect(() => {
    fetchCreditData();
  }, []);

  // Permission gate — placed AFTER all hooks to satisfy react-hooks/rules-of-hooks.
  // The fetchCreditData useEffect will fire and 403 for non-permitted users,
  // which is harmless: the component still renders AccessDenied below.
  if (!hasPermission(user, 'credit_control')) {
    return <AccessDenied permission="Credit Control" />;
  }

  const fetchCreditData = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/credit-limits/all`, {
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        setCustomers(data.customers);
      } else {
        showAlert('Failed to load credit data');
      }
    } catch (error) {
      console.error('Failed to fetch credit data:', error);
      showAlert('Failed to load credit data: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedCustomers = [...customers].sort((a, b) => {
    let aVal = a[sortField];
    let bVal = b[sortField];

    if (typeof aVal === 'string') {
      aVal = aVal.toLowerCase();
      bVal = bVal.toLowerCase();
    }

    if (sortDirection === 'asc') {
      return aVal > bVal ? 1 : -1;
    } else {
      return aVal < bVal ? 1 : -1;
    }
  });

  const filteredCustomers = sortedCustomers
    .filter(c => c.creditLimit > 0)
    .filter(c => filterOverLimit ? c.isOverLimit : true);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const getUtilizationColor = (percentage) => {
    if (percentage >= 100) return 'var(--color-error)';
    if (percentage >= 75) return 'var(--color-warning)';
    if (percentage >= 50) return 'var(--color-success)';
    return 'var(--color-primary)';
  };

  // eslint-disable-next-line no-unused-vars
  const createSnapshots = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/credit-limits/snapshot/all`, {
        method: 'POST',
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        showAlert(data.message);
        fetchCreditData();
      } else {
        showAlert('Failed to create snapshots');
      }
    } catch (error) {
      console.error('Failed to create snapshots:', error);
      showAlert('Failed to create snapshots: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const customersWithCreditLimit = customers.filter(c => c.creditLimit > 0);
  const overLimitCount = customersWithCreditLimit.filter(c => c.isOverLimit).length;
  const totalExposure = customersWithCreditLimit.reduce((sum, c) => sum + c.totalExposure, 0);
  const totalCreditLimit = customersWithCreditLimit.reduce((sum, c) => sum + c.creditLimit, 0);

  return (
    <div className="credit-limit-management">
      <div className="search-header">
        <div className="workflow-info" style={{gridColumn: '1'}}>
          <span className="info">Monitor customer credit limits and exposure in real-time</span>
          <span className="note">
            <span className="material-symbols-rounded">info</span>
            <span style={{margin: 'auto 0'}}>Track unpaid invoices, open purchase orders, and available credit across all customers</span>
          </span>
        </div>
        <div className="search-header-inner" style={{gridColumn: '2'}}>
          <div style={{ display: 'flex', gap: 'var(--spacing-sm)' }}>
            <button
              className={clearFiltersClass(filtersApplied)}
              onClick={() => {
                setSortField('companyName');
                setSortDirection('asc');
                setFilterOverLimit(false);
              }}
              title={filtersApplied ? 'Clear all filters' : 'No filters applied'}
            >
              <div className="icon-wrapper">
                <span className="material-symbols-rounded">filter_alt_off</span>
              </div>
              <span className="btn-label">Clear Filters</span>
            </button>
            <button
              className="btn-slideout"
              onClick={fetchCreditData}
              disabled={loading}
            >
              <div className="icon-wrapper">
                <span className={`material-symbols-rounded ${loading ? 'spinning' : ''}`}>sync</span>
              </div>
              <span className="btn-label">Refresh</span>
            </button>
          </div>
          <div className="search-stats" style={{gridColumn: '1 / 3'}}>
            {customersWithCreditLimit.length} customers with credit limits | {overLimitCount} over limit
          </div>
        </div>
      </div>

      <div className="stats-summary">
        <div className="stat-card">
          <div className="stat-label">Total Customers</div>
          <div className="stat-value">{customersWithCreditLimit.length}</div>
        </div>
        <div className="stat-card warning">
          <div className="stat-label">Over Limit</div>
          <div className="stat-value">{overLimitCount}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Total Credit Limits</div>
          <div className="stat-value">{formatCurrency(totalCreditLimit)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Total Exposure</div>
          <div className="stat-value">{formatCurrency(totalExposure)}</div>
        </div>
      </div>

      <div className="filters">
        <div className="checkbox-container">
          <div className="check light danger">
            <input
              type="checkbox"
              id="filterOverLimit"
              checked={filterOverLimit}
              onChange={(e) => setFilterOverLimit(e.target.checked)}
            />
            <label htmlFor="filterOverLimit"></label>
          </div>
          <span className="filter-label">Show only customers over limit ({overLimitCount})</span>
        </div>
      </div>

      <div className="credit-content-panel">
        <div className="table-container">
          <table className="credit-table">
          <thead>
            <tr>
              <th className="sortable" onClick={() => handleSort('accountNo')}>
                Acc # {sortField === 'accountNo' && (sortDirection === 'asc' ? '▲' : '▼')}
              </th>
              <th className="sortable" onClick={() => handleSort('companyName')}>
                Customer {sortField === 'companyName' && (sortDirection === 'asc' ? '▲' : '▼')}
              </th>
              <th onClick={() => handleSort('creditLimit')} className="text-right sortable">
                 Limit {sortField === 'creditLimit' && (sortDirection === 'asc' ? '▲' : '▼')}
              </th>
              <th onClick={() => handleSort('unpaidInvoicesTotal')} className="text-right sortable">
                Unpaid Invoices {sortField === 'unpaidInvoicesTotal' && (sortDirection === 'asc' ? '▲' : '▼')}
              </th>
              <th onClick={() => handleSort('openPurchaseOrdersTotal')} className="text-right sortable">
                Open Orders {sortField === 'openPurchaseOrdersTotal' && (sortDirection === 'asc' ? '▲' : '▼')}
              </th>
              <th onClick={() => handleSort('unallocatedCreditsTotal')} className="text-right sortable">
                Credits {sortField === 'unallocatedCreditsTotal' && (sortDirection === 'asc' ? '▲' : '▼')}
              </th>
              <th onClick={() => handleSort('totalExposure')} className="text-right sortable">
                Total Exposure {sortField === 'totalExposure' && (sortDirection === 'asc' ? '▲' : '▼')}
              </th>
              <th onClick={() => handleSort('availableCredit')} className="text-right sortable">
                Available {sortField === 'availableCredit' && (sortDirection === 'asc' ? '▲' : '▼')}
              </th>
              <th onClick={() => handleSort('utilizationPercentage')} className="text-center sortable">
                Utilisation {sortField === 'utilizationPercentage' && (sortDirection === 'asc' ? '▲' : '▼')}
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="9">
                  <LoadingSpinner />
                </td>
              </tr>
            ) : filteredCustomers.length === 0 ? (
              <tr>
                <td colSpan="9" className="no-results">
                  {filterOverLimit ? 'No customers over limit' : 'No customers with credit limits'}
                </td>
              </tr>
            ) : (
              filteredCustomers.map((customer) => (
                <tr key={customer.accountId} className={customer.isOverLimit ? 'over-limit' : ''}>
                  <td>
                    <span
                      className="account-link"
                      onClick={() => setSelectedContactId(customer.accountId)}
                    >
                      {customer.accountNo}
                    </span>
                  </td>
                  <td>
                    <span
                      className="company-link"
                      onClick={() => setSelectedContactId(customer.accountId)}
                    >
                      {customer.companyName}
                    </span>
                    {customer.creditGroup?.memberCount > 1 && (
                      <span
                        className="linked-accounts-badge"
                        title={`Shared credit across ${customer.creditGroup.memberCount} linked accounts — figures are the combined total`}
                        onClick={() => setSelectedContactId(customer.accountId)}
                      >
                        <span className="material-symbols-rounded">account_tree</span>
                        {customer.creditGroup.memberCount}
                      </span>
                    )}
                  </td>
                  <td className="text-right">{formatCurrency(customer.creditLimit)}</td>
                  <td className="text-right">{formatCurrency(customer.unpaidInvoicesTotal)}</td>
                  <td className="text-right">{formatCurrency(customer.openPurchaseOrdersTotal)}</td>
                  <td className="text-right credit-amount">
                    {formatCurrency(customer.unallocatedCreditsTotal)}
                  </td>
                  <td className="text-right exposure-amount">
                    {formatCurrency(customer.totalExposure)}
                  </td>
                  <td className="text-right" style={{
                    color: customer.availableCredit < 0 ? 'var(--color-error)' : 'inherit',
                    fontWeight: customer.availableCredit < 0 ? 'bold' : 'normal'
                  }}>
                    {formatCurrency(customer.availableCredit)}
                  </td>
                  <td className="utilization-cell">
                    <div className="utilization-bar">
                      <div
                        className="utilization-fill"
                        style={{
                          width: `${Math.min(customer.utilizationPercentage, 100)}%`,
                          backgroundColor: getUtilizationColor(customer.utilizationPercentage)
                        }}
                      ></div>
                    </div>
                    <span className="utilization-pct" style={{
                      color: getUtilizationColor(customer.utilizationPercentage)
                    }}>
                      {customer.utilizationPercentage.toFixed(0)}%
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      </div>

      {selectedContactId && ReactDOM.createPortal(
        <ContactModal
          contactId={selectedContactId}
          onClose={() => {
            setSelectedContactId(null);
            fetchCreditData();
          }}
        />,
        document.body
      )}
    </div>
  );
}

export default CreditLimitManagement;
